/**
 * lib/assemblePayslip.ts
 * ------------------------------------------------------------------
 * HR PAYROLL — payslip assembly (Phase 1, Day 6).
 *
 * The single source of truth that turns a staff member's inputs into
 * the computed body of a payslip. Used by BOTH the per-staff Payslips
 * tab and the bulk pay run (Day 7), so a slip looks identical however
 * it was produced.
 *
 * Reconciliation guarantee (enforced by assemblePayslipTotals):
 *     net === gross − totalDeductions   (to the thebe)
 *
 * Earnings  = basic + allowances + (gratuity payout, if final slip)
 * Deductions = each active deduction + PAYE + (gratuity tax, if any)
 *
 * PAYE is computed on REGULAR gross (basic + allowances) using the
 * active tax table. The gratuity payout is taxed SEPARATELY by the
 * spread-over-years method (a single gratuity-tax deduction line), per
 * the engine in lib/hrPayroll — it is never run through monthly PAYE.
 * ------------------------------------------------------------------ */
import {
  assemblePayslipTotals,
  pulaInWords,
  monthlyPAYE,
  deductionForPeriod,
  payslipDeductionLabel,
  allowanceCode,
  deductionCode,
  round2,
  MONTH_LABELS,
  type PayslipLine,
  type PayslipPeriod,
  type LeaveBalance,
  type GratuityTaxResult,
  type HrPayrollProfile,
  type HrDeduction,
  type HrTaxTable,
  type HrLeaveAllocation,
  type BankAccountDetails,
} from '@/lib/hrPayroll';
import { staffFullName, type HrStaff } from '@/lib/hr';
import { type LeaveRequestLike } from '@/lib/hrLeave';
import { leaveBalancesFor, type HrLeavePolicy } from '@/lib/leavePolicy';

export interface AssembleInput {
  staff: HrStaff;
  profile?: HrPayrollProfile;
  deductions: HrDeduction[]; // this staff's deductions (any status)
  taxTable?: HrTaxTable; // active table; if absent PAYE = 0
  leaveAllocation?: HrLeaveAllocation;
  leaveRequests: LeaveRequestLike[]; // this staff's leave
  /** Cohort leave policies. Without these the payslip resolves entitlements
   *  from the staff allocation ALONE and disagrees with the leave module —
   *  which is how staff with no per-person sick allocation ended up with a
   *  sick entitlement of nil and a negative balance on their payslip. */
  leavePolicies?: HrLeavePolicy[];
  /** Date the opening balances are "as of"; annual accrues from here.
   *  Comes from payroll settings, as the leave screens do it. */
  leaveAccrualStart?: string;
  period: PayslipPeriod;
  reference: string;
  /** Provide on the final payslip of a contract to add the payout. */
  gratuity?: GratuityTaxResult;
  /**
   * Final-settlement recovery lines (e.g. outstanding loan balances
   * recovered in full at contract end). Added as deduction lines so the
   * net reconciles in one place. Only the workspace's final-pay flow
   * passes these; regular monthly runs leave it undefined.
   */
  recoveries?: { code?: string; label: string; amount: number; remainingBalance?: number }[];
  /** Manual PAYE override for this payslip. When a number (INCLUDING 0) is
   *  provided it REPLACES the table-computed PAYE; undefined/null = compute. */
  payeOverride?: number | null;
  payeOverrideReason?: string;
  /** Editable pay-run cut-offs (Jan..Dec) for the deduction roll-forward. */
  /** First pay month (YYYY-MM) whose payslips may show a sick balance.
   *  Absent = sick is hidden. See HrPayrollSettings.sickBalancesFromMonth. */
  sickBalancesFromMonth?: string;
}

/** The computed body of a payslip (everything except id/status/stamps). */
export interface AssembledPayslip {
  staffId: string;
  staffName: string;
  employeeNo: string;
  jobTitle: string;
  department?: string;
  period: PayslipPeriod;
  reference: string;
  lines: PayslipLine[];
  earningsTotal: number;
  deductionsTotal: number;
  paye: number;
  net: number;
  netInWords: string;
  /** True when paye was manually overridden (incl. an explicit 0). */
  payeOverridden?: boolean;
  payeOverrideReason?: string;
  /** The table-computed PAYE, kept for reference when overridden. */
  computedPaye?: number;
  taxTableId: string;
  taxTableLabel: string;
  leaveBalances: LeaveBalance[];
  gratuity?: GratuityTaxResult;
  bank?: BankAccountDetails;
}

/** Build a deterministic payslip id for a staff-month. */
export function payslipId(staffId: string, period: PayslipPeriod): string {
  return `${staffId}_${period.year}-${String(period.month).padStart(2, '0')}`;
}

/** Build a default month period (1–12) with from/to covering the month. */
export function monthPeriod(year: number, month: number): PayslipPeriod {
  const from = new Date(Date.UTC(year, month - 1, 1)).toISOString();
  const to = new Date(Date.UTC(year, month, 0)).toISOString(); // last day
  return { year, month, from, to };
}

export function periodLabel(period: PayslipPeriod): string {
  return `${MONTH_LABELS[period.month - 1]} ${period.year}`;
}

export function assemblePayslip(input: AssembleInput): AssembledPayslip {
  const { staff, profile, deductions, taxTable, period, reference, gratuity, payeOverride, payeOverrideReason } = input;

  const basic = round2(profile?.basicSalary ?? 0);
  const allowances = profile?.allowances ?? [];

  const lines: PayslipLine[] = [];

  // ---- Earnings ----
  lines.push({ code: 'BASIC', label: 'Basic Salary', kind: 'earning', amount: basic });
  for (const a of allowances) {
    const amt = round2(a.amount || 0);
    if (amt === 0) continue;
    lines.push({ code: allowanceCode(a.type), label: a.type, kind: 'earning', amount: amt });
  }

  // Regular gross = basic + allowances (the PAYE base; excludes gratuity).
  const regularGross = round2(
    basic + allowances.reduce((s, a) => s + (a.amount || 0), 0),
  );

  // Gratuity payout (final slip only) — an earning; taxed separately.
  if (gratuity) {
    lines.push({ code: 'GRATUITY', label: 'Gratuity Payout', kind: 'earning', amount: round2(gratuity.grossGratuity) });
  }

  // ---- Deductions ----
  // Each active deduction (skip PAYE — engine-driven). Finite loans
  // decrement to zero and auto-stop via deductionForPeriod.
  for (const d of deductions) {
    if (d.type === 'paye') continue;
    if (d.oneOffPeriod && (d.oneOffPeriod.year !== period.year || d.oneOffPeriod.month !== period.month)) continue;
    const res = deductionForPeriod(d, period);
    if (res.amount <= 0) continue;
    lines.push({
      code: deductionCode(d.type),
      label: payslipDeductionLabel(d),
      kind: 'deduction',
      amount: res.amount,
      ...(res.remainingAfter !== null ? { remainingBalance: res.remainingAfter } : {}),
    });
  }

  // PAYE on regular gross using the active table, unless manually overridden.
  const computedPaye = taxTable ? monthlyPAYE(regularGross, taxTable) : 0;
  const payeOverridden = payeOverride !== undefined && payeOverride !== null;
  const paye = payeOverridden ? round2(Number(payeOverride)) : computedPaye;
  lines.push({ code: 'PAYE', label: 'PAYE', kind: 'deduction', amount: paye });

  // Gratuity tax (spread-over-years) — separate statutory line.
  if (gratuity) {
    lines.push({ code: 'GRA_TAX', label: 'Gratuity Tax', kind: 'deduction', amount: round2(gratuity.tax) });
  }

  // Final-settlement recoveries (e.g. outstanding loan balances at
  // contract end) — deduction lines so the net reconciles below.
  for (const r of input.recoveries ?? []) {
    const amt = round2(r.amount || 0);
    if (amt <= 0) continue;
    lines.push({
      code: r.code ?? 'RECOVERY', label: r.label, kind: 'deduction', amount: amt,
      ...(typeof r.remainingBalance === 'number' ? { remainingBalance: r.remainingBalance } : {}),
    });
  }

  const totals = assemblePayslipTotals(lines);

  /* LEAVE ON THE PAYSLIP: teaching staff get none.
     Teachers do not draw annual leave — they have the school holidays — so
     the annual row printed an entitlement of 36 days against a remaining
     balance of 0, which invites the obvious question and has no good answer.
     Sick leave was shown to everyone; the school does not want a running sick
     balance on a payslip. So: nothing at all for teaching staff, and annual
     plus sick for everyone else, who do accrue and take both.

     Returning an empty array is enough — payslipPdf omits the whole section
     when there is nothing in it, so no layout special case is needed. */
  const showsLeave = input.staff.category !== 'teaching';
  /* THE SAME SOURCE THE LEAVE MODULE USES.
     This called computeLeaveBalances directly, which takes the staff member's
     allocation at face value. The leave screens call leaveBalancesFor, which
     first resolves entitlements against the COHORT POLICY for their category
     and year. The two therefore disagreed, and a payslip is the worst place
     to learn that: staff with no per-person sick allocation were shown a sick
     entitlement of nil and, once they had taken any, a negative balance.

     Now both go through leaveBalancesFor with the same policies and the same
     accrual start, so the payslip and the leave screen cannot say different
     things about the same person. */
  const rawLeave = showsLeave
    ? leaveBalancesFor({
        category: input.staff.category,
        allocation: input.leaveAllocation,
        requests: input.leaveRequests,
        policies: input.leavePolicies ?? [],
        year: String(input.period.year),
        accrualStart: input.leaveAccrualStart,
        asOf: new Date(input.period.year, input.period.month, 0), // last day of the pay month
        employmentStart: input.staff.startDate,
      })
    : [];

  /* THE THREE FIGURES MUST RECONCILE ON THE PAYSLIP.
     They did not. `entitlement` was the FULL-YEAR allowance while `remaining`
     was the balance ACCRUED SO FAR, so a payslip could read "entitled 36,
     taken 0, balance 3" — three numbers that cannot all be true of the same
     thing, and the first question anyone asks is where the other 33 days went.

     On a payslip the useful figure is what the person has EARNED to date, not
     what they will have earned by December. So the entitled column becomes
     accrued-to-date, derived as `remaining + taken`.

     Deriving it that way rather than recomputing the accrual is deliberate: it
     is the engine's own balance rearranged, so entitled - taken = balance
     holds by construction and cannot drift from lib/hrLeave if the accrual
     rule ever changes. It also carries the opening balance, which a
     recomputation would have to remember to add.

     Only the payslip is changed. The leave screens still show the full annual
     entitlement, which is the right figure when planning a year's leave. */
  /* SICK BALANCES ARE WITHHELD UNTIL THE SCHOOL TRUSTS THEM.
     Annual balances were carried over from the school's own records and are
     good. Sick was never reconciled, so any figure printed against it is a
     guess — and a staff member reads a payslip figure as their entitlement
     and plans against it. Better to show nothing than a number the school
     would have to argue them out of later.

     Governed by settings.sickBalancesFromMonth (YYYY-MM). Absent means
     hidden, because "we do not know yet" is the current honest answer and a
     default that leaks the guess would defeat the point. Annual is never
     affected. */
  const periodKey = `${input.period.year}-${String(input.period.month).padStart(2, '0')}`;
  const sickFrom = input.sickBalancesFromMonth;
  const showsSick = !!sickFrom && periodKey >= sickFrom;

  /* TAKEN MEANS TAKEN THIS MONTH.
     The engine's `taken` is cumulative since the accrual anchor, and its
     `remaining` already has all of that subtracted. So a payslip showing the
     cumulative figure restates months of history the reader does not need,
     and the row does not read as a statement of the month.

     A pay month is a movement:  brought forward - taken this month = balance.
     The balance is the engine's own closing figure, and what was brought
     forward is derived from it as `remaining + takenThisMonth`. Deriving it
     that way means the three figures reconcile by construction and the
     brought-forward figure is automatically net of every earlier month,
     which is precisely what the accrual already accounts for. */
  const takenThisMonth = new Map<string, number>();
  for (const r of input.leaveRequests) {
    if (r.status !== 'approved') continue;
    if (String(r.startDate ?? '').slice(0, 7) !== periodKey) continue;
    takenThisMonth.set(r.type, (takenThisMonth.get(r.type) ?? 0) + (Number(r.days) || 0));
  }

  const leaveBalances = rawLeave
    .filter((b) => showsSick || b.type !== 'sick')
    .map((b) => {
      const taken = round2(takenThisMonth.get(b.type) ?? 0);
      return { ...b, taken, entitlement: round2(b.remaining + taken) };
    });

  return {
    staffId: staff.id,
    staffName: staffFullName(staff),
    employeeNo: staff.employeeNo,
    jobTitle: staff.jobTitle,
    department: staff.department,
    period,
    reference,
    lines,
    earningsTotal: totals.earningsTotal,
    deductionsTotal: totals.deductionsTotal,
    paye: totals.paye,
    net: totals.net,
    netInWords: pulaInWords(totals.net),
    payeOverridden,
    ...(payeOverridden && payeOverrideReason ? { payeOverrideReason } : {}),
    computedPaye,
    taxTableId: taxTable?.id ?? '',
    taxTableLabel: taxTable?.label ?? '(no active tax table)',
    leaveBalances,
    gratuity,
    bank: profile?.bank,
  };
}

/** Convenience: split lines for display. */
export function earningLines(p: { lines: PayslipLine[] }): PayslipLine[] {
  return p.lines.filter((l) => l.kind === 'earning');
}
export function deductionLines(p: { lines: PayslipLine[] }): PayslipLine[] {
  return p.lines.filter((l) => l.kind === 'deduction');
}
